import { createPageRoute } from "./PageRouteDefinition";
import { createRouteSegment } from "./RouteSegment";
import { routeParam, routeTemplate, spaceIdPlaceholder } from "./RouteTemplate";
import { createRedirect } from "./createRedirect";
import {
    booleanQueryParam,
    createArrayQueryParam,
    createQueryParam,
    dateQueryParam,
    numberQueryParam,
    optionalBooleanQueryParam,
    optionalStringQueryParam,
    stringArrayQueryParam,
    stringQueryParam,
    stringUnionQueryParam,
} from "./query/QueryStringParam";
import { spacePartitionQueryParams } from "./spacePartitionQueryParams";

export const insightsSplitParam = createQueryParam("split", validateQuerySplit, (v) => v);
export const insightsCadenceParam = createQueryParam("cadence", validateQueryCadence, (v) => v);
export const insightsTenantTagSetIdParam = optionalStringQueryParam("tenantTagSetId");

export const taskFilterStateParam = createQueryParam("state", validateTaskFilterState, (v) => v);
export const taskListTabParam = createQueryParam("taskListTab", validateTaskListTab, (v) => v);
export const taskDateFilterTypeParam = createQueryParam("dateFilterType", validateTaskDateFilterType, (v) => v);

export const projectInsightsChannelIdParam = optionalStringQueryParam("channelId");
export const projectInsightsEnvironmentIdParam = optionalStringQueryParam("environmentId");
export const projectInsightsTenantIdParam = optionalStringQueryParam("tenantId");

export const machineHealthStatusesParam = createArrayQueryParam("healthStatuses", validateMachineHealthStatus, (v) => v ?? []);
export const communicationStylesParam = createArrayQueryParam("commStyles", validateCommunicationStyle, (v) => v ?? []);

export const environmentsPageQueryParams = [
    stringArrayQueryParam("environmentIds"),
    stringArrayQueryParam("roles"),
    stringArrayQueryParam("tenantIds"),
    stringArrayQueryParam("tenantTags"),
    stringArrayQueryParam("environmentTags"),
    stringArrayQueryParam("targetTags"),
    stringArrayQueryParam("shellNames"),
    stringArrayQueryParam("deploymentTargetTypes"),
    booleanQueryParam("hideEmptyEnvironments"),
    optionalStringQueryParam("machinePartialName"),
    optionalBooleanQueryParam("isDisabled"),
    machineHealthStatusesParam,
    optionalStringQueryParam("partialName"),
] as const;

const sharedMachineFilterParams = [optionalBooleanQueryParam("isDisabled"), machineHealthStatusesParam, optionalStringQueryParam("partialName")] as const;
// Every property name for every page/redirect must be unique across this object.
// This is because `links` flattens this tree down to a flat object, but preserves the property names of pages and redirects.
export const allRoutes = createRouteSegment(routeTemplate``, {
    rootRedirect: createRedirect(routeTemplate`/`),
    system: createRouteSegment(routeTemplate`${spaceIdPlaceholder}`, {
        configuration: createRouteSegment(routeTemplate`/configuration`, {
            configurationRootRedirect: createRedirect(routeTemplate``),
            auditPage: createPageRoute(routeTemplate`/audit`, [
                stringArrayQueryParam("environments"),
                stringArrayQueryParam("users"),
                stringArrayQueryParam("projects"),
                stringArrayQueryParam("projectGroups"),
                stringArrayQueryParam("eventCategories"),
                stringArrayQueryParam("eventGroups"),
                stringArrayQueryParam("eventAgents"),
                stringArrayQueryParam("documentTypes"),
                stringArrayQueryParam("tenants"),
                stringArrayQueryParam("tags"),
                optionalStringQueryParam("from"),
                optionalStringQueryParam("to"),
                stringArrayQueryParam("regardingAny"),
                optionalBooleanQueryParam("includeSystem"),
                stringArrayQueryParam("spaces"),
            ]),
            auditArchivePage: createPageRoute(routeTemplate`/audit-archive`, []),
            backupPage: createPageRoute(routeTemplate`/backup`, []),
            diagnosticsPage: createPageRoute(routeTemplate`/diagnostics`, []),
            diagnosticServerLogsPage: createPageRoute(routeTemplate`/diagnostics/logs`, []),
            diagnosticServerTracesPage: createPageRoute(routeTemplate`/diagnostics/traces`, []),
            autoDeployLogsPage: createPageRoute(routeTemplate`/diagnostics/autodeploy`, []),
            scheduledTriggerLogsPage: createPageRoute(routeTemplate`/diagnostics/scheduleddeploy`, []),
            subscriptionLogsPage: createPageRoute(routeTemplate`/diagnostics/subscription`, []),
            diagnosticMachineCleanupRedirect: createRedirect(routeTemplate`/diagnostics/machinecleanup`),
            featuresPage: createPageRoute(routeTemplate`/features`, []),
            deprecationsPage: createPageRoute(routeTemplate`/deprecations`, []),
            letsEncryptPage: createPageRoute(routeTemplate`/letsencrypt`, []),
            letsEncryptSettingsPage: createPageRoute(routeTemplate`/letsencrypt/configure`, []),
            maintenancePage: createPageRoute(routeTemplate`/maintenance`, []),
            performancePage: createPageRoute(routeTemplate`/performance`, []),
            serverSettingsPage: createPageRoute(routeTemplate`/nodes/serverSettings`, []),
            nodesPage: createPageRoute(routeTemplate`/nodes`, []),
            licensePage: createPageRoute(routeTemplate`/license`, []),
            licenseUsageDashboardPage: createPageRoute(routeTemplate`/licenseUsage`, []),
            smtpPage: createPageRoute(routeTemplate`/smtp`, []),
            gitSettingsPage: createPageRoute(routeTemplate`/git`, []),
            subscriptionsPage: createPageRoute(routeTemplate`/subscriptions`, [stringQueryParam("partialName"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            subscriptionPage: createPageRoute(routeTemplate`/subscriptions/${routeParam("subscriptionId")}`, []),
            createSubscriptionPage: createPageRoute(routeTemplate`/subscriptions/create`, [
                stringArrayQueryParam("environments"),
                stringArrayQueryParam("users"),
                stringArrayQueryParam("projects"),
                stringArrayQueryParam("projectGroups"),
                stringArrayQueryParam("eventCategories"),
                stringArrayQueryParam("eventGroups"),
                stringArrayQueryParam("eventAgents"),
                stringArrayQueryParam("documentTypes"),
                stringArrayQueryParam("tenants"),
                stringArrayQueryParam("tags"),
                optionalStringQueryParam("from"),
                optionalStringQueryParam("to"),
                stringArrayQueryParam("regardingAny"),
                optionalBooleanQueryParam("includeSystem"),
                stringArrayQueryParam("spaces"),
            ]),
            testPermissionsPage: createPageRoute(routeTemplate`/testpermissions`, [stringQueryParam("userId"), stringQueryParam("spaceId"), stringUnionQueryParam("permissionScope", ["System", "Space"], "Space")]),
            apiKeysPage: createPageRoute(routeTemplate`/api-keys`, [
                stringQueryParam("partialPurpose"),
                booleanQueryParam("neverExpire"),
                booleanQueryParam("hasExpired"),
                numberQueryParam("willExpireInDays"),
                stringQueryParam("userId"),
                numberQueryParam("page"),
                numberQueryParam("pageSize"),
            ]),
            thumbprintPage: createPageRoute(routeTemplate`/thumbprint`, []),
            userRolesPage: createPageRoute(routeTemplate`/roles`, [stringQueryParam("roleName"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            createUserRolePage: createPageRoute(routeTemplate`/roles/create`, []),
            userRolePage: createPageRoute(routeTemplate`/roles/${routeParam("roleId")}`, []),
            spacesPage: createPageRoute(routeTemplate`/spaces`, []),
            spacePage: createPageRoute(routeTemplate`/spaces/${routeParam("currentSpaceId")}`, []),
            teamsPage: createPageRoute(routeTemplate`/teams`, [...spacePartitionQueryParams, stringQueryParam("name")]),
            teamPage: createPageRoute(routeTemplate`/teams/${routeParam("teamId")}`, [stringUnionQueryParam("activeTab", ["members", "user roles", "settings"], "members")]),
            usersPage: createPageRoute(routeTemplate`/users`, [booleanQueryParam("isServiceAccount"), stringQueryParam("filter"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            createUserPage: createPageRoute(routeTemplate`/users/create`, []),
            userPage: createPageRoute(routeTemplate`/users/${routeParam("userId")}`, []),
            signingKeysPage: createPageRoute(routeTemplate`/signingkeys`, []),
            telemetryPage: createPageRoute(routeTemplate`/telemetry`, []),
            inviteUsersPage: createPageRoute(routeTemplate`/userinvites`, []),
            scopedUserRoleToTeamRedirect: createRedirect(routeTemplate`/teams/redirect/${routeParam("scopedRoleId")}`),
            settings: createRouteSegment(routeTemplate`/settings`, {
                settingsPage: createPageRoute(routeTemplate``, []),
                activeDirectorySettingsPage: createPageRoute(routeTemplate`/authentication-directoryservices`, []),
                archiveLimitsSettingsPage: createPageRoute(routeTemplate`/archive-limit`, []),
                authenticationSettingsPage: createPageRoute(routeTemplate`/authentication`, []),
                azureADSettingsPage: createPageRoute(routeTemplate`/authentication-aad`, []),
                azureDevOpsIssueTrackerSettingsPage: createPageRoute(routeTemplate`/issuetracker-azuredevops-v2`, []),
                eventRetentionSettingsPage: createPageRoute(routeTemplate`/eventretention`, []),
                githubIssueTrackerSettingsPage: createPageRoute(routeTemplate`/issuetracker-github`, []),
                googleAppsSettingsPage: createPageRoute(routeTemplate`/authentication-googleapps`, []),
                guestLoginSettingsPage: createPageRoute(routeTemplate`/authentication-guest`, []),
                jiraIntegrationSettingsPage: createPageRoute(routeTemplate`/jira-integration`, []),
                jiraServiceManagementIntegrationSettingsPage: createPageRoute(routeTemplate`/jiraservicemanagement-integration`, []),
                octopusApprovalsSettingsPage: createPageRoute(routeTemplate`/octopus-approvals`, []),
                ldapSettingsPage: createPageRoute(routeTemplate`/authentication-ldap`, []),
                machinesSettingsPage: createPageRoute(routeTemplate`/machines`, []),
                octopusIdSettingsPage: createPageRoute(routeTemplate`/authentication-octopusid`, []),
                oktaSettingsPage: createPageRoute(routeTemplate`/authentication-od`, []),
                genericOidcSettingsPage: createPageRoute(routeTemplate`/authentication-generic-oidc`, []),
                retentionPoliciesSettingsPage: createPageRoute(routeTemplate`/retentionpolicies`, []),
                serverFoldersSettingsPage: createPageRoute(routeTemplate`/server-folders`, []),
                serviceNowIntegrationSettingsPage: createPageRoute(routeTemplate`/servicenow-integration`, []),
                upgradeSettingsPage: createPageRoute(routeTemplate`/upgrades`, []),
                usernamePasswordSettingsPage: createPageRoute(routeTemplate`/authentication-usernamepassword`, []),
                webPortalSettingsPage: createPageRoute(routeTemplate`/webportal`, []),
                webRequestProxySettingsPage: createPageRoute(routeTemplate`/server-proxy`, []),
            }),
            deploymentFreezesPage: createPageRoute(routeTemplate`/deploymentfreezes`, []),
            createDeploymentFreezePage: createPageRoute(routeTemplate`/deploymentfreezes/create`, []),
            editDeploymentFreezePage: createPageRoute(routeTemplate`/deploymentfreezes/edit/${routeParam("deploymentFreezeId")}`, []),
        }),
        myProfile: createRouteSegment(routeTemplate`/users/me`, {
            myProfileRootRedirect: createRedirect(routeTemplate``),
            myProfilePage: createPageRoute(routeTemplate`/details`, []),
            myLoginsPage: createPageRoute(routeTemplate`/logins`, []),
            myApiKeysPage: createPageRoute(routeTemplate`/apiKeys`, []),
            myRecentActivityPage: createPageRoute(routeTemplate`/activity`, []),
        }),
        tasksPage: createPageRoute(routeTemplate`/tasks`, [
            stringArrayQueryParam("ids"),
            taskFilterStateParam,
            optionalStringQueryParam("project"),
            optionalStringQueryParam("runbook"),
            optionalStringQueryParam("environment"),
            optionalStringQueryParam("name"),
            optionalStringQueryParam("serverNode"),
            optionalStringQueryParam("tenant"),
            stringArrayQueryParam("spaces"),
            optionalBooleanQueryParam("includeSystem"),
            optionalBooleanQueryParam("hasPendingInterruptions"),
            optionalBooleanQueryParam("hasWarningsOrErrors"),
            taskDateFilterTypeParam,
            dateQueryParam("fromDate"),
            dateQueryParam("toDate"),
            taskListTabParam,
            optionalStringQueryParam("batch"),
        ]),
        approvalsPage: createPageRoute(routeTemplate`/approvals`, [optionalStringQueryParam("providerId"), optionalStringQueryParam("status"), stringArrayQueryParam("spaces"), numberQueryParam("page"), numberQueryParam("pageSize")]),
        taskPage: createPageRoute(routeTemplate`/tasks/${routeParam("taskId")}`, []),
        rawTaskLogPage: createPageRoute(routeTemplate`/tasks/${routeParam("taskId")}/raw`, []),
        scriptConsolePage: createPageRoute(routeTemplate`/tasks/console`, [optionalStringQueryParam("retry")]),
        interruptionToTaskRedirect: createRedirect(routeTemplate`/tasks/interruption/${routeParam("interruptionId")}`),
        styleGuidePage: createPageRoute(routeTemplate`/style-guide`, [optionalStringQueryParam("activeTab")]),
        platformHub: createRouteSegment(routeTemplate`/platform-hub`, {
            platformHubRootRedirect: createRedirect(routeTemplate``),
            platformHubUpsellPage: createPageRoute(routeTemplate`/unlicensed`, []),
            processTemplatesPage: createPageRoute(routeTemplate`/process-templates`, [stringQueryParam("searchValue"), numberQueryParam("page"), numberQueryParam("pageSize"), stringQueryParam("gitRef")]),
            platformHubVersionControlSettingsPage: createPageRoute(routeTemplate`/version-control-settings`, []),
            platformHubAccountsPage: createPageRoute(routeTemplate`/accounts`, []),
            platformHubAddAccountPage: createPageRoute(routeTemplate`/accounts/create`, [optionalStringQueryParam("accountType")]),
            platformHubEditAccountPage: createPageRoute(routeTemplate`/accounts/${routeParam("accountId")}`, []),
            platformHubGitCredentialsPage: createPageRoute(routeTemplate`/git-credentials`, []),
            platformHubCreateGitCredentialPage: createPageRoute(routeTemplate`/git-credentials/create`, []),
            platformHubGitCredentialPage: createPageRoute(routeTemplate`/git-credentials/${routeParam("gitCredentialId")}`, []),
            platformHubGitHubConnectionsPage: createPageRoute(routeTemplate`/git-connections`, []),
            editProcessTemplatePage: createPageRoute(routeTemplate`/process-templates/${routeParam("slug")}`, [
                stringQueryParam("gitRef"),
                optionalBooleanQueryParam("stepTemplates"),
                optionalStringQueryParam("actionId"),
                optionalBooleanQueryParam("new"),
                optionalBooleanQueryParam("childStepTemplates"),
                optionalStringQueryParam("actionType"),
                optionalStringQueryParam("parentStepId"),
                optionalStringQueryParam("templateSpaceId"),
                optionalStringQueryParam("templateId"),
                optionalBooleanQueryParam("isCommunityTemplate"),
                stringUnionQueryParam("activeTab", ["process", "parameters", "settings", "versions"], "process"),
            ]),
            reports: createRouteSegment(routeTemplate`/reports`, {
                complianceReportsPage: createPageRoute(routeTemplate``, []),
                deploymentPermissionsReportPage: createPageRoute(routeTemplate`/deployment-permissions`, [stringArrayQueryParam("environmentIds")]),
            }),
            policiesPage: createPageRoute(routeTemplate`/policies`, [stringQueryParam("searchValue"), numberQueryParam("page"), numberQueryParam("pageSize"), stringQueryParam("gitRef")]),
            editPolicyPage: createPageRoute(routeTemplate`/policies/${routeParam("slug")}`, [stringQueryParam("gitRef"), stringUnionQueryParam("activeTab", ["settings", "versions"], "settings"), optionalBooleanQueryParam("autoPublish")]),
            projectTemplates: createRouteSegment(routeTemplate`/project-templates`, {
                projectTemplatesPage: createPageRoute(routeTemplate``, [stringQueryParam("searchValue"), numberQueryParam("page"), numberQueryParam("pageSize"), stringQueryParam("gitRef")]),
                specificProject: createRouteSegment(routeTemplate`/${routeParam("slug")}`, {
                    branch: createRouteSegment(routeTemplate`/${routeParam("gitRef")}`, {
                        projectTemplateDeploymentProcessPage: createPageRoute(routeTemplate`/process`, [optionalBooleanQueryParam("newlyCreatedProject"), optionalBooleanQueryParam("configureProject"), optionalStringQueryParam("loadSampleProcess")]),
                        projectTemplateDeploymentProcessEditorPage: createPageRoute(routeTemplate`/process/steps`, [
                            optionalBooleanQueryParam("new"),
                            optionalBooleanQueryParam("stepTemplates"),
                            optionalBooleanQueryParam("childStepTemplates"),
                            optionalStringQueryParam("actionType"),
                            optionalStringQueryParam("templateSpaceId"),
                            optionalStringQueryParam("templateId"),
                            optionalBooleanQueryParam("isCommunityTemplate"),
                            optionalStringQueryParam("parentStepId"),
                            optionalStringQueryParam("actionId"),
                            optionalStringQueryParam("actionCategory"),
                        ]),
                        projectTemplateDeploymentProcessStepByStepSlugRedirect: createRedirect(routeTemplate`/process/steps/${routeParam("stepSlug")}`),
                        projectTemplateVersionsPage: createPageRoute(routeTemplate`/versions`, []),
                        variables: createRouteSegment(routeTemplate`/variables`, {
                            projectTemplateVariablesPage: createPageRoute(routeTemplate``, []),
                        }),
                        parameters: createRouteSegment(routeTemplate`/parameters`, {
                            projectTemplateBranchParametersPage: createPageRoute(routeTemplate``, []),
                        }),
                    }),
                }),
            }),
        }),
    }),
    space: createRouteSegment(routeTemplate`/${routeParam("spaceId")}`, {
        spaceRootRedirect: createRedirect(routeTemplate``),
        projectGroupPage: createPageRoute(routeTemplate`/projectGroups/${routeParam("projectGroupId")}`, []),
        releaseRedirect: createRedirect(routeTemplate`/releases/${routeParam("releaseId")}`),
        triggerRedirect: createRedirect(routeTemplate`/triggers/${routeParam("triggerId")}`),
        channelRedirect: createRedirect(routeTemplate`/channels/${routeParam("channelId")}`),
        runbookSnapshotRedirect: createRedirect(routeTemplate`/snapshots/${routeParam("runbookSnapshotId")}`),
        runbookRedirect: createRedirect(routeTemplate`/runbooks/${routeParam("runbookId")}`),
        runbookRunRedirect: createRedirect(routeTemplate`/runs/${routeParam("runbookRunId")}`),
        infrastructure: createRouteSegment(routeTemplate`/infrastructure`, {
            infrastructureRootRedirect: createRedirect(routeTemplate``),
            deploymentTargetsPage: createPageRoute(routeTemplate`/machines`, [
                ...sharedMachineFilterParams,
                stringArrayQueryParam("environmentIds"),
                stringArrayQueryParam("roles"),
                stringArrayQueryParam("tenantIds"),
                stringArrayQueryParam("tenantTags"),
                stringArrayQueryParam("environmentTags"),
                stringArrayQueryParam("targetTags"),
                stringArrayQueryParam("shellNames"),
                stringArrayQueryParam("deploymentTargetTypes"),
                booleanQueryParam("hideEmptyEnvironments"),
                optionalStringQueryParam("machinePartialName"),
            ]),
            selectDeploymentTargetTypePage: createPageRoute(routeTemplate`/machines/new`, [stringQueryParam("environmentId")]),
            addDeploymentTargetPage: createPageRoute(routeTemplate`/machines/create`, [
                optionalStringQueryParam("type"),
                optionalStringQueryParam("deploymentTargetTypeId"),
                optionalStringQueryParam("environment"),
                optionalStringQueryParam("uri"),
                optionalStringQueryParam("thumbprint"),
                optionalStringQueryParam("proxyId"),
                optionalStringQueryParam("host"),
                optionalStringQueryParam("port"),
                optionalStringQueryParam("hostKeyAlgorithm"),
                optionalStringQueryParam("fingerprint"),
            ]),
            deploymentTarget: createRouteSegment(routeTemplate`/machines/${routeParam("machineId")}`, {
                deploymentTargetConnectivityPage: createPageRoute(routeTemplate`/connection`, []),
                deploymentTargetSettingsPage: createPageRoute(routeTemplate`/settings`, []),
                deploymentTargetDeploymentsPage: createPageRoute(routeTemplate`/deployments`, []),
                deploymentTargetRunbookRunsPage: createPageRoute(routeTemplate`/runbookruns`, []),
                deploymentTargetEventsPage: createPageRoute(routeTemplate`/events`, []),
            }),
            discoverDeploymentTargetPage: createPageRoute(routeTemplate`/machines/discover/${routeParam("endpointKey")}`, [optionalStringQueryParam("category"), stringQueryParam("environmentId")]),
            addWorkerPage: createPageRoute(routeTemplate`/workers/create`, [
                optionalStringQueryParam("type"),
                optionalStringQueryParam("workerPool"),
                optionalStringQueryParam("environment"),
                optionalStringQueryParam("uri"),
                optionalStringQueryParam("thumbprint"),
                optionalStringQueryParam("proxyId"),
                optionalStringQueryParam("host"),
                optionalStringQueryParam("port"),
                optionalStringQueryParam("hostKeyAlgorithm"),
                optionalStringQueryParam("fingerprint"),
            ]),
            workersPage: createPageRoute(routeTemplate`/workers`, [
                ...sharedMachineFilterParams,
                stringArrayQueryParam("workerPoolIds"),
                stringArrayQueryParam("shellNames"),
                optionalStringQueryParam("machinePartialName"),
                stringArrayQueryParam("roles"),
                booleanQueryParam("hideEmptyWorkerPools"),
                communicationStylesParam,
            ]),
            selectWorkerTypePage: createPageRoute(routeTemplate`/workers/new`, [optionalStringQueryParam("workerPoolId")]),
            discoverWorkerPage: createPageRoute(routeTemplate`/workers/discover/${routeParam("endpointKey")}`, [optionalStringQueryParam("category"), stringQueryParam("workerPoolId")]),
            workerMachine: createRouteSegment(routeTemplate`/workers/${routeParam("machineId")}`, {
                workerConnectivityPage: createPageRoute(routeTemplate`/connection`, []),
                workerSettingsPage: createPageRoute(routeTemplate`/settings`, []),
                workerEventsPage: createPageRoute(routeTemplate`/events`, []),
            }),
            environmentPage: createPageRoute(routeTemplate`/environments/${routeParam("environmentId")}`, []),
            environmentsPage: createPageRoute(routeTemplate`/environments`, [...environmentsPageQueryParams]),
            infrastructureOverviewPage: createPageRoute(routeTemplate`/overview`, []),
            parentEnvironmentPage: createPageRoute(routeTemplate`/parentEnvironments/${routeParam("environmentId")}`, []),
            workerPoolsPage: createPageRoute(routeTemplate`/workerpools`, [
                ...sharedMachineFilterParams,
                stringArrayQueryParam("workerPoolIds"),
                stringArrayQueryParam("shellNames"),
                optionalStringQueryParam("machinePartialName"),
                stringArrayQueryParam("roles"),
                booleanQueryParam("hideEmptyWorkerPools"),
                communicationStylesParam,
            ]),
            workerPoolPage: createPageRoute(routeTemplate`/workerpools/${routeParam("workerPoolId")}`, []),
            machinePoliciesPage: createPageRoute(routeTemplate`/machinepolicies`, [stringQueryParam("machinePolicyName"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            machinePolicyPage: createPageRoute(routeTemplate`/machinepolicies/${routeParam("machinePolicyId")}`, []),
            createMachinePolicyPage: createPageRoute(routeTemplate`/machinepolicies/create`, []),
            machineProxiesPage: createPageRoute(routeTemplate`/proxies`, [stringQueryParam("machineProxyName"), numberQueryParam("page"), numberQueryParam("pageSize")]),

            machineProxyPage: createPageRoute(routeTemplate`/proxies/${routeParam("proxyId")}`, []),
            addMachineProxyPage: createPageRoute(routeTemplate`/proxies/create`, []),

            argoCDInstancesPage: createPageRoute(routeTemplate`/argocdinstances`, [stringArrayQueryParam("environmentIds"), stringQueryParam("instanceName")]),
            argoCDInstance: createRouteSegment(routeTemplate`/argocdinstances/${routeParam("instanceId")}`, {
                argoCDInstanceSettingsPage: createPageRoute(routeTemplate`/settings`, [optionalBooleanQueryParam("openAnnotationsDrawer")]),
                argoCDInstanceApplicationsPage: createPageRoute(routeTemplate`/applications`, [
                    stringQueryParam("name"),
                    stringArrayQueryParam("projectIds"),
                    stringArrayQueryParam("environmentIds"),
                    stringArrayQueryParam("tenantIds"),
                    optionalBooleanQueryParam("hideUnannotated"),
                    numberQueryParam("pageNumber"),
                ]),
                argoCDInstanceConnectivityPage: createPageRoute(routeTemplate`/connectivity`, []),
            }),
            accountsPage: createPageRoute(routeTemplate`/accounts`, []),
            accountPage: createPageRoute(routeTemplate`/accounts/${routeParam("accountId")}`, []),
            addAccountPage: createPageRoute(routeTemplate`/accounts/create`, [optionalStringQueryParam("accountType")]),
        }),
        projects: createRouteSegment(routeTemplate`/projects`, {
            specificProject: createRouteSegment(routeTemplate`/${routeParam("projectSlug")}`, {
                branch: createRouteSegment(routeTemplate`/branches/${routeParam("branchName")}`, {
                    deployments: createRouteSegment(routeTemplate`/deployments`, {
                        branchDeploymentProcessPage: createPageRoute(routeTemplate`/process`, [optionalBooleanQueryParam("newlyCreatedProject"), optionalBooleanQueryParam("configureProject"), optionalStringQueryParam("loadSampleProcess")]),
                        branchDeploymentProcessEditorPage: createPageRoute(routeTemplate`/process/steps`, [
                            optionalBooleanQueryParam("new"),
                            optionalBooleanQueryParam("stepTemplates"),
                            optionalBooleanQueryParam("childStepTemplates"),
                            optionalStringQueryParam("actionType"),
                            optionalStringQueryParam("templateSpaceId"),
                            optionalStringQueryParam("templateId"),
                            optionalBooleanQueryParam("isCommunityTemplate"),
                            optionalStringQueryParam("parentStepId"),
                            optionalStringQueryParam("actionId"),
                            optionalBooleanQueryParam("newlyCreatedProject"),
                            optionalStringQueryParam("actionCategory"),
                            stringArrayQueryParam("tags"),
                        ]),
                        branchDeploymentProcessStepByStepSlugRedirect: createRedirect(routeTemplate`/process/steps/${routeParam("stepSlug")}`),
                        branchDeploymentSettingsPage: createPageRoute(routeTemplate`/settings`, []),
                    }),
                    variables: createRouteSegment(routeTemplate`/variables`, {
                        branchProjectVariablesPage: createPageRoute(routeTemplate``, []),
                        branchAllVariablesPage: createPageRoute(routeTemplate`/all`, []),
                        branchVariablePreviewPage: createPageRoute(routeTemplate`/preview`, []),
                        branchTemplateParametersPage: createPageRoute(routeTemplate`/template-parameters`, []),
                    }),
                }),
                withoutBranch: createRouteSegment(routeTemplate``, {
                    projectRootRedirect: createRedirect(routeTemplate``),
                    projectOverviewRedirect: createRedirect(routeTemplate`/overview`),
                    deployments: createRouteSegment(routeTemplate`/deployments`, {
                        process: createRouteSegment(routeTemplate`/process`, {
                            deploymentProcessPage: createPageRoute(routeTemplate``, [optionalBooleanQueryParam("newlyCreatedProject"), optionalBooleanQueryParam("configureProject"), optionalStringQueryParam("loadSampleProcess")]),
                            deploymentProcessEditorPage: createPageRoute(routeTemplate`/steps`, [
                                optionalBooleanQueryParam("new"),
                                optionalBooleanQueryParam("stepTemplates"),
                                optionalBooleanQueryParam("childStepTemplates"),
                                optionalStringQueryParam("actionType"),
                                optionalStringQueryParam("templateSpaceId"),
                                optionalStringQueryParam("templateId"),
                                optionalBooleanQueryParam("isCommunityTemplate"),
                                optionalStringQueryParam("parentStepId"),
                                optionalStringQueryParam("actionId"),
                                optionalBooleanQueryParam("newlyCreatedProject"),
                                optionalStringQueryParam("actionCategory"),
                                optionalStringQueryParam("processTemplateSlug"),
                                optionalStringQueryParam("processTemplateVersionMask"),
                                optionalStringQueryParam("processTemplateActionId"),
                                optionalStringQueryParam("processTemplateParentStepId"),
                                stringArrayQueryParam("tags"),
                            ]),
                            deploymentProcessStepByStepSlugRedirect: createRedirect(routeTemplate`/steps/${routeParam("stepSlug")}`),
                        }),
                        settings: createRouteSegment(routeTemplate`/settings`, {
                            deploymentSettingsPage: createPageRoute(routeTemplate``, []),
                        }),
                        projectDashboardPage: createPageRoute(routeTemplate``, [optionalBooleanQueryParam("newlyCreatedProject"), optionalBooleanQueryParam("configureProject")]),
                        channelsPage: createPageRoute(routeTemplate`/channels`, []),
                        createChannelPage: createPageRoute(routeTemplate`/channels/create`, []),
                        channelPage: createPageRoute(routeTemplate`/channels/edit/${routeParam("channelId")}`, []),
                        releasesPage: createPageRoute(routeTemplate`/releases`, []),
                        createReleasePage: createPageRoute(routeTemplate`/releases/create`, [optionalStringQueryParam("channelId")]),
                        specificRelease: createRouteSegment(routeTemplate`/releases/${routeParam("releaseVersion")}`, {
                            deploymentPage: createPageRoute(routeTemplate`/deployments/${routeParam("deploymentId")}`, [optionalStringQueryParam("activeTab")]),
                            editReleasePage: createPageRoute(routeTemplate`/edit`, []),
                            copyReleasePage: createPageRoute(routeTemplate`/copy`, []),
                            releasePage: createPageRoute(routeTemplate``, []),
                            deployReleasePage: createPageRoute(routeTemplate`/deployments/create`, [
                                stringArrayQueryParam("environmentIds"),
                                stringArrayQueryParam("tenantIds"),
                                stringArrayQueryParam("tenantTags"),
                                optionalStringQueryParam("previousDeploymentId"),
                            ]),
                        }),
                    }),
                    variables: createRouteSegment(routeTemplate`/variables`, {
                        // Here we create an additional route segment at the same location as the project variables page.
                        // This is because we want to wrap the project variables page with a branch redirect component.
                        // However we don't want this to apply to all sub routes of the variables partial route, (e.g. the variables library route is not version controlled so should not redirect).
                        // So this additional partial route layer allows us to isolate this redirect behaviour to just the routes that need it.
                        wrapper: createRouteSegment(routeTemplate``, {
                            projectVariablesPage: createPageRoute(routeTemplate``, []),
                        }),
                        all: createRouteSegment(routeTemplate`/all`, {
                            allVariablesPage: createPageRoute(routeTemplate``, []),
                        }),
                        preview: createRouteSegment(routeTemplate`/preview`, {
                            variablePreviewPage: createPageRoute(routeTemplate``, []),
                        }),
                        tenant: createRouteSegment(routeTemplate`/tenant`, {
                            projectTenantsRedirect: createRedirect(routeTemplate``),
                            tenantVariablesProjectTemplatesPage: createPageRoute(routeTemplate`/project-templates`, []),
                            tenantVariablesCommonTemplatesPage: createPageRoute(routeTemplate`/common-templates`, []),
                        }),
                        projectVariableSetsPage: createPageRoute(routeTemplate`/library`, []),
                        templateParametersPage: createPageRoute(routeTemplate`/template-parameters`, []),
                    }),
                    untenantedApplicationLiveStatusPage: createPageRoute(routeTemplate`/environments/${routeParam("environmentId")}/untenanted/livestatus`, []),
                    tenantedApplicationLiveStatusPage: createPageRoute(routeTemplate`/environments/${routeParam("environmentId")}/tenants/${routeParam("tenantId")}/livestatus`, []),
                    projectInsightsOverviewPage: createPageRoute(routeTemplate`/insights`, [projectInsightsChannelIdParam, projectInsightsEnvironmentIdParam, insightsCadenceParam, projectInsightsTenantIdParam]),
                    projectLeadTimeInsightsPage: createPageRoute(routeTemplate`/insights/lead-time`, [projectInsightsChannelIdParam, projectInsightsEnvironmentIdParam, insightsCadenceParam, projectInsightsTenantIdParam]),
                    projectDeploymentFrequencyInsightsPage: createPageRoute(routeTemplate`/insights/frequency`, [projectInsightsChannelIdParam, projectInsightsEnvironmentIdParam, insightsCadenceParam, projectInsightsTenantIdParam]),
                    projectFailureRateInsightsPage: createPageRoute(routeTemplate`/insights/failure-rate`, [projectInsightsChannelIdParam, projectInsightsEnvironmentIdParam, insightsCadenceParam, projectInsightsTenantIdParam]),
                    projectTimeToRecoveryInsightsPage: createPageRoute(routeTemplate`/insights/mttr`, [projectInsightsChannelIdParam, projectInsightsEnvironmentIdParam, insightsCadenceParam, projectInsightsTenantIdParam]),
                    projectDeploymentDurationInsightsPage: createPageRoute(routeTemplate`/insights/deployment-duration`, [projectInsightsChannelIdParam, projectInsightsEnvironmentIdParam, insightsCadenceParam, projectInsightsTenantIdParam]),
                    operationsPage: createPageRoute(routeTemplate`/operations`, []),
                    runbookTriggersPage: createPageRoute(routeTemplate`/operations/triggers`, [
                        optionalStringQueryParam("searchText"),
                        optionalBooleanQueryParam("showDeploymentTargetTriggers"),
                        optionalBooleanQueryParam("showScheduledTriggers"),
                        optionalStringQueryParam("runbookId"),
                    ]),
                    createRunbookScheduledTriggerPage: createPageRoute(routeTemplate`/operations/triggers/scheduled/create/runbooks`, [stringQueryParam("runbookId")]),
                    runbookScheduledTriggerPage: createPageRoute(routeTemplate`/operations/triggers/scheduled/edit/${routeParam("triggerId")}`, []),
                    projectRunbooks: createRouteSegment(routeTemplate`/operations/runbooks`, {
                        runbooksPage: createPageRoute(routeTemplate``, [stringQueryParam("gitRef"), stringArrayQueryParam("runbookTags")]),
                    }),
                    specificRunbook: createRouteSegment(routeTemplate`/operations/runbooks/${routeParam("runbookId")}`, {
                        runbookRootRedirect: createRedirect(routeTemplate``),
                        runbookOverviewPage: createPageRoute(routeTemplate`/overview`, []),
                        runbookRunsPage: createPageRoute(routeTemplate`/runslist`, [stringQueryParam("gitRef")]),
                        runbookSettingsPage: createPageRoute(routeTemplate`/settings`, [stringQueryParam("gitRef")]),
                        runbookSnapshotsPage: createPageRoute(routeTemplate`/snapshots`, []),
                        process: createRouteSegment(routeTemplate`/process`, {
                            runbookProcessPage: createPageRoute(routeTemplate`/${routeParam("processId")}`, [stringQueryParam("gitRef"), optionalStringQueryParam("loadSampleProcess")]),
                            runbookProcessEditorPage: createPageRoute(routeTemplate`/${routeParam("processId")}/steps`, [
                                stringQueryParam("gitRef"),
                                optionalBooleanQueryParam("new"),
                                optionalBooleanQueryParam("stepTemplates"),
                                optionalBooleanQueryParam("childStepTemplates"),
                                optionalStringQueryParam("actionType"),
                                optionalStringQueryParam("templateSpaceId"),
                                optionalStringQueryParam("templateId"),
                                optionalBooleanQueryParam("isCommunityTemplate"),
                                optionalStringQueryParam("parentStepId"),
                                optionalStringQueryParam("actionId"),
                            ]),
                            projectRunbookProcessStepByStepSlugRedirect: createRedirect(routeTemplate`/${routeParam("processId")}/steps/${routeParam("stepSlug")}`),
                        }),
                        runRunbookPage: createPageRoute(routeTemplate`/snapshots/${routeParam("runbookSnapshotId")}/runs/create`, [
                            stringArrayQueryParam("environmentIds"),
                            optionalStringQueryParam("previousRunbookRunId"),
                            stringQueryParam("gitRef"),
                        ]),
                        runbookRunNowPage: createPageRoute(routeTemplate`/runNow`, [stringQueryParam("gitRef")]),
                        runbookRunSnapshotPage: createPageRoute(routeTemplate`/runNow/${routeParam("runbookSnapshotId")}`, [stringQueryParam("gitRef")]),
                        publishNewSnapshotPage: createPageRoute(routeTemplate`/snapshots/create`, []),
                        editRunbookSnapshotPage: createPageRoute(routeTemplate`/snapshots/${routeParam("runbookSnapshotId")}/edit`, []),
                        runbookSnapshotPage: createPageRoute(routeTemplate`/snapshots/${routeParam("runbookSnapshotId")}`, []),
                        runbookRunPage: createPageRoute(routeTemplate`/snapshots/${routeParam("runbookSnapshotId")}/runs/${routeParam("runbookRunId")}`, []),
                    }),
                    projectTasksPage: createPageRoute(routeTemplate`/tasks`, [
                        stringArrayQueryParam("ids"),
                        taskFilterStateParam,
                        optionalStringQueryParam("runbook"),
                        optionalStringQueryParam("environment"),
                        optionalStringQueryParam("name"),
                        optionalStringQueryParam("serverNode"),
                        optionalStringQueryParam("tenant"),
                        stringArrayQueryParam("spaces"),
                        optionalBooleanQueryParam("hasPendingInterruptions"),
                        optionalBooleanQueryParam("hasWarningsOrErrors"),
                        taskDateFilterTypeParam,
                        dateQueryParam("fromDate"),
                        dateQueryParam("toDate"),
                        taskListTabParam,
                        optionalStringQueryParam("batch"),
                    ]),
                    projectTaskPage: createPageRoute(routeTemplate`/tasks/${routeParam("taskId")}`, []),
                    ephemeralEnvironmentsPage: createPageRoute(routeTemplate`/ephemeral-environments`, [
                        numberQueryParam("page"),
                        numberQueryParam("pageSize"),
                        stringQueryParam("environmentName"),
                        stringUnionQueryParam("status", ["all", "active", "pending", "failed", "inactive"], "all"),
                    ]),
                    ephemeralEnvironmentsSettingsPage: createPageRoute(routeTemplate`/ephemeral-environments/settings`, []),
                    projectTenantsPage: createPageRoute(routeTemplate`/tenants`, []),
                    triggersPage: createPageRoute(routeTemplate`/triggers`, []),
                    createDeploymentTargetTriggerPage: createPageRoute(routeTemplate`/triggers/create`, []),
                    createReleaseCreationFeedTriggerPage: createPageRoute(routeTemplate`/triggers/feed/create`, []),
                    releaseCreationFeedTriggerPage: createPageRoute(routeTemplate`/triggers/feed/edit/${routeParam("triggerId")}`, []),
                    createReleaseCreationGitTriggerPage: createPageRoute(routeTemplate`/triggers/git/create`, []),
                    releaseCreationGitTriggerPage: createPageRoute(routeTemplate`/triggers/git/edit/${routeParam("triggerId")}`, []),
                    createBuiltInPackageRepositoryTriggerPage: createPageRoute(routeTemplate`/triggers/built-in-feed/create`, []),
                    builtInFeedTriggerPage: createPageRoute(routeTemplate`/triggers/built-in-feed/edit`, []),
                    deploymentTargetTriggerPage: createPageRoute(routeTemplate`/triggers/edit/${routeParam("triggerId")}`, []),
                    createDeploymentScheduledTriggerPage: createPageRoute(routeTemplate`/triggers/scheduled/create`, []),
                    deploymentScheduledTriggerPage: createPageRoute(routeTemplate`/triggers/scheduled/edit/${routeParam("triggerId")}`, []),
                    projectDeploymentFreezesPage: createPageRoute(routeTemplate`/deploymentfreezes`, []),
                    createProjectDeploymentFreezePage: createPageRoute(routeTemplate`/deploymentfreezes/create`, []),
                    projectDeploymentFreezePage: createPageRoute(routeTemplate`/deploymentfreezes/edit/${routeParam("deploymentFreezeId")}`, []),
                    projectSettingsPage: createPageRoute(routeTemplate`/settings`, []),
                    projectVersionControlSettingsPage: createPageRoute(routeTemplate`/settings/versioncontrol`, [optionalBooleanQueryParam("newlyCreatedProject"), optionalBooleanQueryParam("configureProject")]),
                    itsmProvidersSettingsPage: createPageRoute(routeTemplate`/settings/itsmproviders`, []),
                    featureTogglesPage: createPageRoute(routeTemplate`/featuretoggles`, [stringQueryParam("toggleName"), numberQueryParam("page"), numberQueryParam("pageSize")]),
                    featureTogglePage: createPageRoute(routeTemplate`/featuretoggles/${routeParam("featureToggleSlug")}`, []),
                }),
            }),
            projectsPage: createPageRoute(routeTemplate``, [stringQueryParam("searchValue"), numberQueryParam("page"), numberQueryParam("pageSize"), optionalStringQueryParam("includeProjectGroupId")]),
            importExports: createRouteSegment(routeTemplate`/importExport`, {
                importExportTasksPage: createPageRoute(routeTemplate`/overview`, []),
                importProjectsPage: createPageRoute(routeTemplate`/import`, []),
                importProjectsTaskPage: createPageRoute(routeTemplate`/import/${routeParam("taskId")}`, []),
                exportProjectsPage: createPageRoute(routeTemplate`/export`, []),
                exportProjectsTaskPage: createPageRoute(routeTemplate`/export/${routeParam("taskId")}`, []),
            }),
        }),
        deploymentToProjectTaskRedirect: createRedirect(routeTemplate`/deployments/${routeParam("deploymentId")}`),
        tenants: createRouteSegment(routeTemplate`/tenants`, {
            tenantsPage: createPageRoute(routeTemplate``, [
                stringQueryParam("search"),
                stringQueryParam("excludedSearch"),
                stringQueryParam("project"),
                stringQueryParam("excludedProject"),
                stringQueryParam("environment"),
                stringQueryParam("excludedEnvironment"),
                stringArrayQueryParam("tags"),
                stringArrayQueryParam("excludedTags"),
                numberQueryParam("pageNumber"),
                booleanQueryParam("includeMissingVariablesStatus"),
            ]),
            specificTenant: createRouteSegment(routeTemplate`/${routeParam("tenantId")}`, {
                tenantOverviewPage: createPageRoute(routeTemplate`/overview`, []),
                tenantVariablesPage: createPageRoute(routeTemplate`/variables`, [stringUnionQueryParam("activeTab", ["commonVariables", "projectVariables"], "projectVariables")]),
                tenantSettingsPage: createPageRoute(routeTemplate`/settings`, []),
                tenantTasksPage: createPageRoute(routeTemplate`/tasks`, []),
            }),
        }),
        library: createRouteSegment(routeTemplate`/library`, {
            libraryRootRedirect: createRedirect(routeTemplate``),
            builtInPackageRepositoryPage: createPageRoute(routeTemplate`/builtinrepository`, [stringQueryParam("filter"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            packageDetailsPage: createPageRoute(routeTemplate`/builtinrepository/${routeParam("id")}`, []),
            packageVersionsPage: createPageRoute(routeTemplate`/builtinrepository/versions/${routeParam("packageId")}`, [stringQueryParam("filter"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            buildInformationRepositoryPage: createPageRoute(routeTemplate`/buildinformation`, [stringQueryParam("partialPackageId"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            buildInformationVersionsPage: createPageRoute(routeTemplate`/buildinformation/versions/${routeParam("packageId")}`, []),
            buildInformationDetailsPage: createPageRoute(routeTemplate`/buildinformation/${routeParam("id")}`, []),
            externalFeedsPage: createPageRoute(routeTemplate`/feeds`, [stringQueryParam("feedName"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            createFeedPage: createPageRoute(routeTemplate`/feeds/create`, []),
            externalFeedPage: createPageRoute(routeTemplate`/feeds/${routeParam("feedId")}`, []),
            testExternalFeedPage: createPageRoute(routeTemplate`/feeds/${routeParam("feedId")}/test`, []),
            redirectFeedBasedOnType: createRedirect(routeTemplate`/feeds/${routeParam("feedId")}/redirect`),
            lifecyclesPage: createPageRoute(routeTemplate`/lifecycles`, [stringQueryParam("lifecycleName"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            createLifecyclePage: createPageRoute(routeTemplate`/lifecycles/create`, []),
            lifecyclePage: createPageRoute(routeTemplate`/lifecycles/${routeParam("lifecycleId")}`, [stringUnionQueryParam("activeTab", ["details", "usage"], "details")]),
            scriptModulesPage: createPageRoute(routeTemplate`/scripts`, [stringQueryParam("scriptModuleName"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            createScriptModulePage: createPageRoute(routeTemplate`/scripts/create`, []),
            scriptModulePage: createPageRoute(routeTemplate`/scripts/${routeParam("scriptModuleId")}`, [stringUnionQueryParam("activeTab", ["summary", "usage"], "summary")]),
            stepTemplatesPage: createPageRoute(routeTemplate`/steptemplates`, [stringQueryParam("templateName"), stringUnionQueryParam("templateType", ["all", "community", "custom"], "all"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            selectBuiltInStepTemplatePage: createPageRoute(routeTemplate`/steptemplates/builtin`, []),
            communityStepTemplatesPage: createPageRoute(routeTemplate`/steptemplates/community`, []),
            communityStepTemplatePage: createPageRoute(routeTemplate`/steptemplates/community/${routeParam("templateId")}`, [stringUnionQueryParam("activeTab", ["parameters", "sourceCode"], "parameters")]),
            createStepTemplatePage: createPageRoute(routeTemplate`/steptemplates/new/${routeParam("actionType")}`, [stringUnionQueryParam("activeTab", ["step", "settings", "parameters"], "settings")]),
            runStepTemplatePage: createPageRoute(routeTemplate`/steptemplates/${routeParam("templateId")}/run`, [optionalStringQueryParam("retry")]),
            specificStepTemplate: createRouteSegment(routeTemplate`/steptemplates/${routeParam("templateId")}`, {
                stepTemplateDetailsPage: createPageRoute(routeTemplate``, [stringUnionQueryParam("activeTab", ["step", "settings", "parameters"], "step")]),
                stepTemplateUsagePage: createPageRoute(routeTemplate`/usage`, []),
            }),
            tagSetsPage: createPageRoute(routeTemplate`/tagsets`, [stringQueryParam("tagSetName"), stringArrayQueryParam("scopes"), stringArrayQueryParam("types"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            createTagSetPage: createPageRoute(routeTemplate`/tagsets/create`, []),
            tagSetPage: createPageRoute(routeTemplate`/tagsets/${routeParam("tagSetId")}`, []),
            variableSetsPage: createPageRoute(routeTemplate`/variables`, [stringQueryParam("variableSetName"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            variableSetPage: createPageRoute(routeTemplate`/variables/${routeParam("variableSetId")}`, [stringUnionQueryParam("activeTab", ["variables", "variableTemplates", "usages", "settings"], "variables")]),
            gitCredentialsPage: createPageRoute(routeTemplate`/gitcredentials`, [stringQueryParam("gitCredentialName"), numberQueryParam("page"), numberQueryParam("pageSize")]),
            createGitCredentialPage: createPageRoute(routeTemplate`/gitcredentials/create`, []),
            gitCredentialPage: createPageRoute(routeTemplate`/gitcredentials/${routeParam("gitCredentialId")}`, []),
            gitHubConnectionsPage: createPageRoute(routeTemplate`/gitconnections`, []),
            addGitHubConnectionPage: createPageRoute(routeTemplate`/gitconnections/add/github/${routeParam("installationId")}`, []),
            gitHubConnectionPage: createPageRoute(routeTemplate`/gitconnections/github/${routeParam("connectionId")}`, []),
            addCertificatePage: createPageRoute(routeTemplate`/certificates/create`, []),
            certificatePage: createPageRoute(routeTemplate`/certificates/${routeParam("certificateId")}`, []),
            certificatesPage: createPageRoute(routeTemplate`/certificates`, [stringQueryParam("search"), numberQueryParam("page"), numberQueryParam("pageSize"), optionalBooleanQueryParam("archived")]),
            retentionPoliciesPage: createPageRoute(routeTemplate`/retentionpolicies`, []),
            builtInPackageRetentionPoliciesPage: createPageRoute(routeTemplate`/retentionpolicies/builtinpackage`, []),
            releaseRetentionPoliciesPage: createPageRoute(routeTemplate`/retentionpolicies/release`, []),
            runbookRetentionPoliciesPage: createPageRoute(routeTemplate`/retentionpolicies/runbook`, []),
            packageCacheRetentionPoliciesPage: createPageRoute(routeTemplate`/retentionpolicies/packagecache`, []),

            approvalPoliciesPage: createPageRoute(routeTemplate`/approvalpolicies`, [numberQueryParam("page"), numberQueryParam("pageSize")]),
            createApprovalPolicyPage: createPageRoute(routeTemplate`/approvalpolicies/create`, []),
            approvalPolicyPage: createPageRoute(routeTemplate`/approvalpolicies/${routeParam("id")}`, []),
        }),
        insights: createRouteSegment(routeTemplate`/insights`, {
            insightsRootRedirect: createRedirect(routeTemplate``),
            insightsReportUpsellPage: createPageRoute(routeTemplate`/unlicensed`, []),

            reports: createRouteSegment(routeTemplate`/reports`, {
                insightReportListPage: createPageRoute(routeTemplate``, []),
                specificReport: createRouteSegment(routeTemplate`/${routeParam("reportId")}`, {
                    insightsReportOverviewPage: createPageRoute(routeTemplate`/overview`, [insightsSplitParam, insightsCadenceParam, insightsTenantTagSetIdParam]),
                    deploymentFrequencyInsightsPage: createPageRoute(routeTemplate`/frequency`, [insightsSplitParam, insightsCadenceParam, insightsTenantTagSetIdParam]),
                    deploymentLeadTimeInsightsPage: createPageRoute(routeTemplate`/lead-time`, [insightsSplitParam, insightsCadenceParam, insightsTenantTagSetIdParam]),
                    failureRateInsightsPage: createPageRoute(routeTemplate`/failure-rate`, [insightsSplitParam, insightsCadenceParam, insightsTenantTagSetIdParam]),
                    meanTimeToRecoveryInsightsPage: createPageRoute(routeTemplate`/mttr`, [insightsSplitParam, insightsCadenceParam, insightsTenantTagSetIdParam]),
                    deploymentDurationInsightsPage: createPageRoute(routeTemplate`/deployment-duration`, [insightsSplitParam, insightsCadenceParam, insightsTenantTagSetIdParam]),
                    insightsReportSettingsPage: createPageRoute(routeTemplate`/settings`, []),
                }),
            }),
        }),
    }),
});

export function exhaustiveFallback<T>(param: never, fallback: T): T {
    return fallback;
}

function validateQuerySplit(serializedValue: string | undefined): InsightsMetricsSplit | undefined {
    if (serializedValue === undefined) return undefined;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const possibleSplit = serializedValue as InsightsMetricsSplit;
    return Object.values(InsightsMetricsSplitValues).includes(possibleSplit) ? possibleSplit : undefined;
}

function validateQueryCadence(queryCadence: string | undefined): InsightsCadence | undefined {
    if (queryCadence === undefined) return undefined;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const possibleCadence = queryCadence as InsightsCadence;
    return Object.values(InsightsCadenceValues).includes(possibleCadence) ? possibleCadence : undefined;
}

export const InsightsMetricsSplitValues = {
    None: "None",
    Project: "Project",
    ProjectGroup: "ProjectGroup",
    Environment: "Environment",
    EnvironmentGroup: "EnvironmentGroup",
    Tenant: "Tenant",
    TenantTagSet: "TenantTagSet",
} as const;

export type InsightsMetricsSplit = (typeof InsightsMetricsSplitValues)[keyof typeof InsightsMetricsSplitValues];

export const InsightsCadenceValues = {
    LastYearMonthly: "lastYearMonthly",
    LastYearWeekly: "lastYearWeekly",
    LastQuarterWeekly: "lastQuarterWeekly",
    LastQuarterDaily: "lastQuarterDaily",
    LastMonthDaily: "lastMonthDaily",
} as const;

export type InsightsCadence = (typeof InsightsCadenceValues)[keyof typeof InsightsCadenceValues];

export const EndpointRegistrationKeyValues = {
    CloudRegion: "CloudRegion",
    TentaclePassive: "TentaclePassive",
    TentacleActive: "TentacleActive",
    Ssh: "Ssh",
    OfflineDrop: "OfflineDrop",
    AzureWebApp: "AzureWebApp",
    AzureCloudService: "AzureCloudService",
    AzureServiceFabricCluster: "AzureServiceFabricCluster",
    AzureVmExtension: "AzureVmExtension",
    Kubernetes: "Kubernetes",
    StepPackage: "StepPackage",
    KubernetesTentacle: "KubernetesTentacle",
    AwsEcsCluster: "aws-ecs-target",
};

export type EndpointRegistrationKey = (typeof EndpointRegistrationKeyValues)[keyof typeof EndpointRegistrationKeyValues];

export interface GitRefQueryParams {
    gitRef: string;
}

export type ProjectInsightsFilterQueryParams = {
    channelId?: string;
    environmentId?: string;
    tenantId?: string;
    cadence?: InsightsCadence;
};

export const TaskFilterStateValues = {
    Incomplete: "Incomplete",
    Running: "Running",
    Completed: "Completed",
    Unsuccessful: "Unsuccessful",
    Queued: "Queued",
    Executing: "Executing",
    Cancelling: "Cancelling",
    Success: "Success",
    Canceled: "Canceled",
    TimedOut: "TimedOut",
    Failed: "Failed",
} as const;
export type TaskFilterState = (typeof TaskFilterStateValues)[keyof typeof TaskFilterStateValues];

export const TaskListTabValues = {
    All: "All",
    Running: "Running",
    Waiting: "Waiting",
    Completed: "Completed",
} as const;
export type TaskListTab = (typeof TaskListTabValues)[keyof typeof TaskListTabValues];

export const TaskDateFilterTypeValues = {
    QueueTime: "Queue Time",
    StartTime: "Start Time",
    CompletedTime: "Completed Time",
} as const;
export type TaskDateFilterType = (typeof TaskDateFilterTypeValues)[keyof typeof TaskDateFilterTypeValues];

function validateTaskFilterState(serializedValue: string | undefined): TaskFilterState | undefined {
    if (serializedValue === undefined) return undefined;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const possibleSplit = serializedValue as TaskFilterState;
    return Object.values(TaskFilterStateValues).includes(possibleSplit) ? possibleSplit : undefined;
}

function validateTaskListTab(serializedValue: string | undefined): TaskListTab | undefined {
    if (serializedValue === undefined) return undefined;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const possibleSplit = serializedValue as TaskListTab;
    return Object.values(TaskListTabValues).includes(possibleSplit) ? possibleSplit : undefined;
}

function validateTaskDateFilterType(serializedValue: string | undefined): TaskDateFilterType | undefined {
    if (serializedValue === undefined) return undefined;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const possibleSplit = serializedValue as TaskDateFilterType;
    return Object.values(TaskDateFilterTypeValues).includes(possibleSplit) ? possibleSplit : undefined;
}

export const MachineHealthStatusValues = {
    Healthy: "Healthy",
    Unavailable: "Unavailable",
    Unknown: "Unknown",
    HasWarnings: "HasWarnings",
    Unhealthy: "Unhealthy",
} as const;
export type MachineHealthStatus = (typeof MachineHealthStatusValues)[keyof typeof MachineHealthStatusValues];

export const CommunicationStyleValues = {
    None: "None",
    TentaclePassive: "TentaclePassive",
    TentacleActive: "TentacleActive",
    Ssh: "Ssh",
    OfflineDrop: "OfflineDrop",
    AzureWebApp: "AzureWebApp",
    AzureCloudService: "AzureCloudService",
    AzureServiceFabricCluster: "AzureServiceFabricCluster",
    Kubernetes: "Kubernetes",
    KubernetesTentacle: "KubernetesTentacle",
    StepPackage: "StepPackage",
} as const;
export type CommunicationStyle = (typeof CommunicationStyleValues)[keyof typeof CommunicationStyleValues];

function validateMachineHealthStatus(serializedValues: string[]): MachineHealthStatus[] {
    return serializedValues
        .map((value) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const possibleSplit = value as MachineHealthStatus;
            return Object.values(MachineHealthStatusValues).includes(possibleSplit) ? possibleSplit : null;
        })
        .filter((v): v is MachineHealthStatus => v !== null);
}

function validateCommunicationStyle(serializedValues: string[]): CommunicationStyle[] {
    return serializedValues
        .map((value) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const possibleSplit = value as CommunicationStyle;
            return Object.values(CommunicationStyleValues).includes(possibleSplit) ? possibleSplit : null;
        })
        .filter((v): v is CommunicationStyle => v !== null);
}
